import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import api from "@/lib/api";
import { EASE, Reveal, SectionHead } from "./shared";
import { scrollToId } from "@/lib/lenis";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/blog")
      .then((res) => setPosts(res.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="blog" className="parchment-texture relative py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead
          light
          kicker="Diario di Bordo"
          title="Note di scrittura dal lago"
          sub="Leggende vere, dietro le quinte e i luoghi del romanzo: il diario che accompagna la strada verso novembre 2026."
        />

        {loading ? (
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-80 animate-pulse rounded-xl bg-lake-teal/10" />
            ))}
          </div>
        ) : (
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3" data-testid="blog-grid">
            {posts.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.1}>
                <motion.article
                  whileHover={{ y: -8 }}
                  transition={{ type: "spring", stiffness: 180, damping: 18 }}
                  className="card-parchment group h-full cursor-pointer overflow-hidden rounded-xl"
                  onClick={() => setSelected(p)}
                  data-testid={`blog-card-${p.slug}`}
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={p.immagine}
                      alt=""
                      className="cinematic h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <span className="absolute left-4 top-4 rounded-full bg-lake-deep/85 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-gold-shimmer backdrop-blur-sm">
                      {p.categoria}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-xl leading-snug text-ink-dark">{p.titolo}</h3>
                    <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-ink-muted">{p.estratto}</p>
                    <div className="mt-5 flex items-center justify-between">
                      <span className="text-xs text-ink-muted">
                        {p.data} · {p.tempo}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-lake-teal">
                        Leggi
                        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </motion.article>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-lake-deep/80 p-4 backdrop-blur-sm"
            onClick={() => setSelected(null)}
            data-testid="blog-modal"
          >
            <motion.article
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="card-parchment max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-xl"
              onClick={(e) => e.stopPropagation()}
              data-lenis-prevent
            >
              <div className="relative h-56">
                <img src={selected.immagine} alt="" className="cinematic h-full w-full object-cover" />
                <button
                  data-testid="blog-modal-close"
                  onClick={() => setSelected(null)}
                  aria-label="Chiudi articolo"
                  className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-lake-deep/85 text-parchment-ivory backdrop-blur-sm transition-colors hover:bg-lake-deep"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="p-6 sm:p-10">
                <span className="rounded-full border border-gold/50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-lake-teal">
                  {selected.categoria}
                </span>
                <h3 className="mt-4 font-serif text-2xl leading-snug text-ink-dark sm:text-3xl">
                  {selected.titolo}
                </h3>
                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-ink-muted">
                  {selected.data} · {selected.tempo} · di Patrizia Franzina
                </p>
                <div className="gold-line my-6 w-full opacity-60" />
                <div className="space-y-5">
                  {selected.corpo.map((par, i) => (
                    <p key={i} className={`leading-relaxed text-ink-dark/85 ${i === 0 ? "font-serif text-lg" : "text-[15px]"}`}>
                      {par}
                    </p>
                  ))}
                </div>
                <button
                  data-testid="blog-modal-newsletter-cta"
                  onClick={() => {
                    setSelected(null);
                    scrollToId("contatti");
                  }}
                  className="mt-8 w-full rounded-full bg-lake-deep px-6 py-3 text-sm font-semibold text-parchment-ivory transition-transform duration-300 hover:-translate-y-0.5 sm:w-auto"
                >
                  Iscriviti al Diario di Bordo
                </button>
              </div>
            </motion.article>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
